import { MantineThemeComponents } from '@mantine/core';

// Breakpoint definitions
export const breakpoints = {
	mobile: 768,
	tablet: 1024,
} as const;

// Theme-specific breakpoints for component props
// 14" screens typically start around 1366px width
export const themeBreakpoints = {
	mobile: 768,
	tablet: 1366, // 14" and above treated as desktop for component props
} as const;

// Responsive component configuration
export const responsiveComponentConfig = {
	defaultRadius: {
		mobile: 'sm',
		tablet: 'md',
		desktop: 'md',
	},
	components: {
		Avatar: {
			styles: {
				root: {
					border: '1px solid var(--color-border)',
				},
				image: {
					objectFit: 'cover',
				},
			},
		},
		Switch: {
			defaultProps: {
				size: {
					mobile: 'xs',
					tablet: 'xs',
					desktop: 'sm',
				},
			},
		},
		Checkbox: {
			defaultProps: {
				size: {
					mobile: 'xs',
					tablet: 'xs',
					desktop: 'sm',
				},
			},
		},
		Paper: {
			defaultProps: {
				withBorder: {
					mobile: true,
					tablet: true,
					desktop: true,
				},
				p: {
					mobile: 'md',
					tablet: 'md',
					desktop: 'md',
				},
			},
			styles: {
				root: {
					backgroundColor: 'var(--color-surface)',
					borderColor: 'var(--color-border)',
					color: 'var(--color-text)',
				},
			},
		},
		Badge: {
			defaultProps: {
				color: {
					mobile: 'gray',
					tablet: 'gray',
					desktop: 'gray',
				},
				size: {
					mobile: 'sm',
					tablet: 'xs',
					desktop: 'sm',
				},
			},
		},
		Card: {
			defaultProps: {
				withBorder: true,
				p: {
					mobile: 'xs',
					tablet: 'md',
					desktop: 'md',
				},
			},
			styles: {
				root: {
					backgroundColor: 'var(--color-surface)',
					borderColor: 'var(--color-border)',
					color: 'var(--color-text)',
				},
			},
		},
		Title: {
			defaultProps: {
				order: {
					mobile: 2,
					tablet: 2,
					desktop: 2,
				},
			},
		},
		Button: {
			defaultProps: {
				size: {
					mobile: 'xs',
					tablet: 'xs',
					desktop: 'sm',
				},
				radius: {
					mobile: 'xs',
					tablet: 'sm',
					desktop: 'sm',
				},
			},
		},
		TextInput: {
			defaultProps: {
				size: {
					mobile: 'sm',
					tablet: 'sm',
					desktop: 'sm',
				},
			},
		},
		Text: {
			defaultProps: {
				size: {
					mobile: 'xs',
					tablet: 'xs',
					desktop: 'sm',
				},
			},
		},
		PasswordInput: {
			defaultProps: {
				size: {
					mobile: 'sm',
					tablet: 'sm',
					desktop: 'sm',
				},
			},
		},
		Modal: {
			styles: {
				header: {
					backgroundColor: 'var(--color-surface)',
				},
			},
		},
	},
} as const;

// Helper function to extract values for a specific breakpoint
function extractBreakpointConfig(
	config: typeof responsiveComponentConfig,
	breakpoint: 'mobile' | 'tablet' | 'desktop'
): {
	defaultRadius: string;
	components: MantineThemeComponents;
} {
	const components: MantineThemeComponents = {};

	// Extract component configurations for the specified breakpoint
	for (const [componentName, componentConfig] of Object.entries(config.components)) {
		components[componentName] = {
			defaultProps: {},
		};

		if ('defaultProps' in componentConfig && componentConfig.defaultProps) {
			for (const [propName, propValue] of Object.entries(componentConfig.defaultProps)) {
				if (
					typeof propValue === 'object' &&
					propValue !== null &&
					breakpoint in propValue
				) {
					components[componentName].defaultProps[propName] =
						propValue[breakpoint as keyof typeof propValue];
				}
			}
		}

		// Add styles if they exist (styles are the same for all breakpoints)
		if ('styles' in componentConfig && componentConfig.styles) {
			components[componentName].styles = componentConfig.styles;
		}
	}

	return {
		defaultRadius: config.defaultRadius[breakpoint],
		components,
	};
}

// Export component configurations for each breakpoint
export const componentConfig = {
	mobile: extractBreakpointConfig(responsiveComponentConfig, 'mobile'),
	tablet: extractBreakpointConfig(responsiveComponentConfig, 'tablet'),
	desktop: extractBreakpointConfig(responsiveComponentConfig, 'desktop'),
} as const;
